import React from 'react';
import { ArrowRight, Award, BadgeCheck, Check } from 'lucide-react';
import { ScrollReveal } from './ScrollReveal';

const SYSTEM_PRICE = '$9,499';

const INCLUDED_FEATURES = [
  'Remote access',
  'Back office',
  'Installation',
  'Remote price change',
  'Tank monitoring',
  'Price sign board connection',
  'Safe drops',
  'A/R account tracking',
  'Employee cash card',
  'Individual employee logins & timesheets',
  'House account tracking',
  'Presets at pump level (helps save keypad life)',
];

/**
 * Ad-focused offer + certifications — placed directly below the hero.
 */
const HomeAdOffer: React.FC = () => {
  return (
    <>
      <section
        id="complete-system"
        className="bg-white border-y border-gray-200 py-10 sm:py-12 md:py-16"
      >
        <ScrollReveal>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center mb-8 sm:mb-10">
              <p className="text-sm sm:text-base font-semibold uppercase tracking-wide text-gray-500 mb-2">
                Complete fuel &amp; c-store system
              </p>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                Everything included. One price.
              </h2>
              <p className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 tracking-tight">
                {SYSTEM_PRICE}
              </p>
              <p className="mt-2 text-base sm:text-lg text-gray-600">
                Complete system price — installation included
              </p>
            </div>

            <div className="max-w-5xl mx-auto rounded-2xl border border-gray-200 bg-gray-50 p-5 sm:p-6 md:p-8 shadow-sm">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-5 text-center sm:text-left">
                Included in price
              </h3>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                {INCLUDED_FEATURES.map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-sm sm:text-base text-gray-700">
                    <Check className="h-5 w-5 shrink-0 text-green-600 mt-0.5" aria-hidden />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href="/contact"
                className="bg-gray-900 text-white px-8 py-3.5 rounded-lg font-semibold text-base hover:bg-gray-800 transition-colors inline-flex items-center justify-center gap-2 min-h-[48px] touch-manipulation"
              >
                Get a Quote
                <ArrowRight className="h-5 w-5" />
              </a>
              <a
                href="tel:+12018460591"
                className="border-2 border-gray-900 text-gray-900 px-8 py-3.5 rounded-lg font-semibold text-base hover:bg-gray-900 hover:text-white transition-colors inline-flex items-center justify-center min-h-[48px] touch-manipulation"
              >
                Call (201) 846-0591
              </a>
            </div>
          </div>
        </ScrollReveal>
      </section>

      <section className="bg-gray-900 py-8 sm:py-10 md:py-12">
        <ScrollReveal>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 max-w-4xl mx-auto">
              <div className="flex gap-4 rounded-xl border border-white/15 bg-white/10 p-5 sm:p-6 backdrop-blur-sm">
                <Award className="h-10 w-10 shrink-0 text-white" aria-hidden />
                <div>
                  <p className="text-xs sm:text-sm font-semibold uppercase tracking-wide text-gray-400 mb-1">
                    Certified partner
                  </p>
                  <p className="text-base sm:text-lg font-semibold text-white leading-snug">
                    Certified by Dover Fueling Solutions
                  </p>
                </div>
              </div>

              <div className="flex gap-4 rounded-xl border border-white/15 bg-white/10 p-5 sm:p-6 backdrop-blur-sm">
                <BadgeCheck className="h-10 w-10 shrink-0 text-white" aria-hidden />
                <div>
                  <p className="text-xs sm:text-sm font-semibold uppercase tracking-wide text-gray-400 mb-1">
                    Exclusive reseller
                  </p>
                  <p className="text-base sm:text-lg font-semibold text-white leading-snug">
                    The official and only reseller for Bulloch Technologies on the East Coast
                  </p>
                </div>
              </div>
            </div>
          </div>
        </ScrollReveal>
      </section>
    </>
  );
};

export default HomeAdOffer;
